import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { db } from "../../lib/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { useSubscription } from "../../hooks/useSubscription";
import { useAccessControl } from "../../hooks/useAccessControl";
import AccessBlockBanner from "../../components/AccessBlockBanner";
import EmailVerificationBanner from "../../components/EmailVerificationBanner";
import InstructorFeatureGuard from "../../components/InstructorFeatureGuard";
import { 
  PlusIcon,
  BellIcon
} from "../../components/icons/OutlineIcons";
import { BookOpen, Search, XCircle, ArrowLeft } from "lucide-react";

interface Course {
  id: string;
  courseId?: string;
  title: string;
  thumbnail: string;
  price: number;
}

export default function MyCourses() {
  const { user, loading: authLoading, emailVerified } = useAuthContext();
  const { canCreate, getMaxCourses, plan } = useSubscription();
  const { hasAccess, blockReason, blockMessage, isLoading: accessLoading } = useAccessControl();

  // Redirect if not authenticated or not instructor/admin
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!authLoading && user?.role !== "instructor" && user?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  // Expired users can see courses but features will be disabled

  // ไม่ redirect ถ้า email ไม่ verified - ให้เข้าหน้าได้แต่แสดง Banner แทน

  const [courses, setCourses] = useState<Course[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function loadCourses() {
      if (!user) return;

      const q = query(
        collection(db, "courses"),
        where("instructorId", "==", user.uid)
      );
      const snap = await getDocs(q);

      const data = snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      } as Course));

      setCourses(data);
    }

    loadCourses();
  }, [user]);

  const filteredCourses = courses.filter(course => {
    if (!searchQuery.trim()) return true;
    
    const searchLower = searchQuery.toLowerCase();
    const title = (course.title || '').toLowerCase();
    const courseId = (course.courseId || '').toLowerCase();
    
    return title.includes(searchLower) || courseId.includes(searchLower);
  });

  const canCreateNewCourse = canCreate(courses.length);
  const maxCourses = getMaxCourses();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 pb-20">
      <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-8">
        {/* Back Button */}
        <Link to="/instructor/dashboard" className="inline-flex items-center gap-1.5 md:gap-2 text-blue-600 hover:text-blue-700 mb-4 md:mb-6 font-semibold text-sm md:text-base transition-colors">
          <ArrowLeft className="w-4 md:w-5 h-4 md:h-5" />
          กลับไปหน้า Dashboard
        </Link>

        {/* Header */}
        <div className="mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-1 md:mb-2">
            คอร์สของฉัน
          </h1>
          <p className="text-textLight text-sm md:text-lg">
            จัดการและแก้ไขคอร์สของคุณ
          </p>
        </div>

        {/* Access Block Banner */}
        {!hasAccess && !accessLoading && (
          <AccessBlockBanner blockReason={blockReason} blockMessage={blockMessage} />
        )}

        {/* Subscription Limit Warning */}
        {!canCreateNewCourse && (
          <div className="mb-4 md:mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl md:rounded-2xl p-4 md:p-6">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-10 md:w-12 h-10 md:h-12 bg-yellow-100 rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0">
                <BellIcon className="w-6 md:w-7 h-6 md:h-7 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-textDark text-sm md:text-base mb-1.5 md:mb-2">
                  ถึงขีดจำกัดคอร์สแล้ว
                </h3>
                <p className="text-textLight text-xs md:text-sm mb-3 md:mb-4">
                  คุณสร้างคอร์สได้ {courses.length}/{maxCourses === "unlimited" ? "∞" : maxCourses} คอร์ส
                  {maxCourses !== "unlimited" && " ในแพ็กเกจ " + plan?.name}
                </p>
                <Link to="/subscription/renew">
                  <button className="bg-gradient-primary text-white px-4 md:px-6 py-2 rounded-lg md:rounded-xl text-sm md:text-base font-semibold hover:shadow-lg transition-all">
                    อัปเกรดแพ็กเกจ
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Course List Section */}
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-textDark">รายการคอร์ส</h2>
              <p className="text-xs md:text-sm text-textLight mt-0.5 md:mt-1">
                {maxCourses === "unlimited" 
                  ? `${courses.length} คอร์ส`
                  : `${courses.length}/${maxCourses} คอร์ส`
                } • แพ็กเกจ {plan?.name}
                {searchQuery && ` • พบ ${filteredCourses.length} รายการ`}
              </p>
            </div>
            {canCreateNewCourse && hasAccess ? (
              <Link to="/instructor/courses/new">
                <button className="bg-gradient-primary text-white hover:shadow-large transition-all duration-300 transform hover:scale-105 px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold flex items-center gap-1.5 md:gap-2">
                  <PlusIcon className="w-4 md:w-5 h-4 md:h-5" />
                  <span className="hidden sm:inline">สร้างคอร์สใหม่</span>
                  <span className="sm:hidden">สร้าง</span>
                </button>
              </Link>
            ) : (
              <div className="relative group">
                <button
                  disabled
                  className="bg-gray-300 text-gray-500 px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold cursor-not-allowed flex items-center gap-1.5 md:gap-2"
                >
                  <PlusIcon className="w-4 md:w-5 h-4 md:h-5" />
                  <span className="hidden sm:inline">สร้างคอร์สใหม่</span>
                  <span className="sm:hidden">สร้าง</span>
                </button>
                {!hasAccess && (
                  <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    {blockMessage}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Search Box */}
          {courses.length > 0 && (
            <div className="mb-4 md:mb-6 relative">
              <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาคอร์ส (ชื่อหรือรหัสคอร์ส)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 md:pl-12 pr-10 md:pr-12 py-2 md:py-3 text-sm md:text-base border-2 border-gray-200 rounded-lg md:rounded-xl focus:border-primary focus:outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-4 md:w-5 h-4 md:h-5" />
                </button>
              )}
            </div>
          )}

          {courses.length === 0 && !searchQuery && (
            <div className="bg-gradient-soft rounded-xl md:rounded-2xl p-8 md:p-12 text-center">
              <div className="flex justify-center mb-3 md:mb-4">
                <div className="w-16 md:w-20 h-16 md:h-20 bg-blue-100 rounded-xl md:rounded-2xl flex items-center justify-center">
                  <BookOpen className="w-8 md:w-10 h-8 md:h-10 text-blue-600" />
                </div>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-textDark mb-1.5 md:mb-2">
                ยังไม่มีคอร์ส
              </h3>
              <p className="text-sm md:text-base text-textLight mb-4 md:mb-6">
                เริ่มสร้างคอร์สแรกของคุณเลย!
              </p>
              {canCreateNewCourse && hasAccess && (
                <Link to="/instructor/courses/new">
                  <button className="bg-gradient-primary text-white hover:shadow-large transition-all px-6 md:px-8 py-2.5 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold">
                    + สร้างคอร์สแรก
                  </button>
                </Link>
              )}
              {!hasAccess && (
                <div className="relative group inline-block">
                  <button
                    disabled
                    className="bg-gray-300 text-gray-500 px-6 md:px-8 py-2.5 md:py-3 rounded-lg md:rounded-xl text-sm md:text-base font-semibold cursor-not-allowed"
                  >
                    + สร้างคอร์สแรก
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                    {blockMessage}
                  </div>
                </div>
              )}
            </div>
          )}

          {courses.length > 0 && filteredCourses.length === 0 && searchQuery && (
            <div className="bg-gray-50 rounded-xl md:rounded-2xl p-8 md:p-12 text-center">
              <div className="flex justify-center mb-3 md:mb-4">
                <div className="w-16 md:w-20 h-16 md:h-20 bg-gray-100 rounded-xl md:rounded-2xl flex items-center justify-center">
                  <Search className="w-8 md:w-10 h-8 md:h-10 text-gray-400" />
                </div>
              </div>
              <h3 className="text-lg md:text-xl font-bold text-textDark mb-1.5 md:mb-2">
                ไม่พบคอร์สที่ค้นหา
              </h3>
              <p className="text-sm md:text-base text-textLight mb-3 md:mb-4">
                ลองค้นหาด้วยคำอื่นหรือรหัสคอร์สอื่น
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-primary text-sm md:text-base font-semibold hover:underline"
              >
                ล้างการค้นหา
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((c) => (
              hasAccess ? (
                <Link
                  key={c.id}
                  to={`/instructor/courses/${c.id}/edit`}
                  className="group"
                >
                  <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-soft hover:shadow-large transition-all duration-300 transform hover:scale-105 border-2 border-gray-100 hover:border-blue-200">
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={c.thumbnail}
                        alt={c.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-2 md:top-3 right-2 md:right-3 bg-white/90 backdrop-blur-sm px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                        <span className="text-xs md:text-sm font-bold text-primary">
                          ฿{c.price}
                        </span>
                      </div>
                      {c.courseId && (
                        <div className="absolute top-2 md:top-3 left-2 md:left-3 bg-blue-600/90 backdrop-blur-sm px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                          <span className="text-[10px] md:text-xs font-bold text-white">
                            {c.courseId}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-bold text-sm md:text-base text-textDark mb-1.5 md:mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                        {c.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-textLight">0 นักเรียน</span>
                        <span className="text-primary font-semibold group-hover:translate-x-1 transition-transform">
                          แก้ไข →
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ) : (
                <div key={c.id} className="relative group cursor-not-allowed">
                  <div className="bg-gray-100 rounded-xl md:rounded-2xl overflow-hidden shadow-soft border-2 border-gray-200 opacity-60">
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={c.thumbnail}
                        alt={c.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 md:top-3 right-2 md:right-3 bg-white/90 backdrop-blur-sm px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                        <span className="text-xs md:text-sm font-bold text-primary">
                          ฿{c.price}
                        </span>
                      </div>
                      {c.courseId && (
                        <div className="absolute top-2 md:top-3 left-2 md:left-3 bg-blue-600/90 backdrop-blur-sm px-2 md:px-3 py-0.5 md:py-1 rounded-full">
                          <span className="text-[10px] md:text-xs font-bold text-white">
                            {c.courseId}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-bold text-sm md:text-base text-gray-600 mb-1.5 md:mb-2 line-clamp-2">
                        {c.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs md:text-sm">
                        <span className="text-gray-500">0 นักเรียน</span>
                        <span className="text-gray-500 font-semibold">
                          ไม่สามารถแก้ไข
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/5 rounded-xl md:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                      {blockMessage}
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
